import { graphql } from 'react-apollo'

import {
  ErrorDetails,
  GenericAnalyticsClient,
  withAnalyticsClient,
} from '@acorns/web-utils'
import { reduce, values } from 'ramda'
import { compose, withHandlers, withProps, withState } from 'recompose'

import { ModalActionTypes } from 'components/ConfirmationModal'
import { trackGraphQLError } from 'utils/analytics'
import { withApolloErrorModal } from 'utils/error'

import AlertBadge from '../../../assets/icons/alert-badge.png'
import legacyUploadDocumentMutation from './legacyUploadDocument.mutation.gql'

type ProgressMap = { [key: number]: number }

type StateProps = {
  analytics: GenericAnalyticsClient
  closeErrorModal: () => void
  failed: boolean
  progressMap: ProgressMap
  setFailed: (val: boolean) => void
  setProgressMap: (val: ProgressMap) => void
  showErrorModal: (error: ErrorDetails) => void
  uploadDocuments: any
}

type CalculatedProps = {
  progress: number
}

type HandlerPropsTop = {
  setFileProgress: (index: number, percent: number) => void
}

export type UploadHandlers = {
  uploadFile: (documentType: string, index: number, file: File) => void
  reset: () => void
}

const calculateProgress = (progressMap: ProgressMap) => {
  const progressValues = values(progressMap)
  return (
    reduce((acc, value) => acc + value, 0, progressValues) /
    (progressValues.length || 1)
  )
}

export const withUpload = compose(
  withAnalyticsClient,
  withApolloErrorModal,
  graphql(legacyUploadDocumentMutation, {
    name: 'uploadDocuments',
  }),
  withState('progressMap', 'setProgressMap', {}),
  withState('failed', 'setFailed', false),
  withProps<CalculatedProps, StateProps>(({ progressMap }) => ({
    progress: calculateProgress(progressMap),
  })),
  withHandlers<StateProps, HandlerPropsTop>({
    setFileProgress:
      ({ progressMap, setProgressMap }) =>
      (index: number, percent: number) => {
        setProgressMap({
          ...progressMap,
          [index]: percent,
        })
      },
  }),
  withHandlers<StateProps & HandlerPropsTop, UploadHandlers>({
    uploadFile:
      ({
        setFileProgress,
        setProgressMap,
        showErrorModal,
        closeErrorModal,
        setFailed,
        analytics,
        uploadDocuments,
      }) =>
      (documentType, index, file) => {
        const handleCloseErrorModal = () => {
          closeErrorModal()
          setFailed(false)
        }

        return uploadDocuments({
          variables: {
            documentType,
            upload: file,
          },
        })
          .then(() => {
            setFileProgress(index, 1)
          })
          .catch((error: any) => {
            setProgressMap({})
            setFailed(true)
            showErrorModal({
              actions: [
                {
                  handler: handleCloseErrorModal,
                  label: 'Ok',
                  type: ModalActionTypes.danger,
                },
              ],
              body: 'Sorry, we are having issues connecting to our servers. Please try again later.',
              header: 'Oops!',
              image: AlertBadge,
            })

            trackGraphQLError(
              analytics,
              'mutation',
              'legacyUploadDocument',
              error && error.response ? error.response.status : null,
              error,
            )
            throw error
          })
      },
    reset:
      ({ setProgressMap }) =>
      () => {
        setProgressMap({})
      },
  }),
)
